import React, { useEffect, useRef, useState, useMemo } from 'react';
import { 
    Box, 
    Paper,
    Container,
    Menu,
    MenuItem,
    Divider
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useScrollPosition } from '../../hooks/useScrollPosition';

// Import focused hooks for SOLID architecture
import { useFilters } from '../../state/hooks/useFilters';
import { useUiState } from '../../state/hooks/useUiState';
import { useActiveTab, useFilteredBeers } from '../../state/hooks/useBeerSelectors';
import { buildFilterClearedEvent, useAnalyticsService } from '../../services/analytics';
import { getSelectedCardIds, toggleGlobalCardSelection, clearGlobalSelection } from '../BeerCard/BeerCardContainer';

// Import the new BeerStatsAndSettings component
import { BeerStatsAndSettings } from '../BeerStatsAndSettings';

/**
 * ActiveFiltersBar - displays active filters and beer count
 * This version is integrated with our context system
 * Uses hooks for scroll position tracking to become sticky
 */
/**
 * ActiveFiltersBar component - displays active filters and beer count
 * Refactored to use focused hooks following SOLID principles
 */
const ActiveFiltersBar: React.FC = () => {
    const analyticsService = useAnalyticsService();
    const filteredBeers = useFilteredBeers();
    const [updateTrigger, setUpdateTrigger] = useState(0);

    // Use focused hooks for filters
    const { 
        filters: {
            searchTerm,
            typeFilter,
            drinkTypeFilter,
            breweryFilter,
            tagFilters,
            showOnlyFavorites
        },
        setSearchTerm,
        setTypeFilter,
        setDrinkTypeFilter,
        setBreweryFilter,
        setShowOnlyFavorites,
        removeTagFilter,
        clearAllFilters
    } = useFilters();
    
    // Get active tab to hide filters on My Beers Tasting List view
    const activeTab = useActiveTab();
    const isMyBeersTab = activeTab === '1';
    
    // Track scroll position to make bar sticky
    // Using a lower threshold (10px) so it sticks almost immediately when scrolling
    const isSticky = useScrollPosition(10);
    
    // State for filter dropdown menu
    const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
    const filterMenuOpen = Boolean(filterMenuAnchor);
    
    const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setFilterMenuAnchor(event.currentTarget);
    };
    
    const handleFilterMenuClose = () => {
        setFilterMenuAnchor(null);
    };
    
    // Poll for selection changes to trigger re-renders
    useEffect(() => {
        const interval = setInterval(() => {
            setUpdateTrigger(prev => prev + 1);
        }, 100);
        return () => clearInterval(interval);
    }, []);
    
    // Check if all filtered beers are selected
    // Note: Component re-renders periodically via interval, causing this to recalculate
    const allFilteredSelected = useMemo(() => {
        if (filteredBeers.length === 0) return false;
        const selected = getSelectedCardIds();
        return filteredBeers.every(beer => selected.has(beer.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredBeers, updateTrigger]);
    
    // Handle select/deselect all filtered beers
    const handleToggleSelectAll = () => {
        if (filteredBeers.length === 0) return;
        
        if (allFilteredSelected) {
            // Deselect all
            clearGlobalSelection();
            analyticsService.trackEvent({
                name: 'filter_cleared',
                params: {
                    filter_type: 'selection'
                }
            });
        } else {
            // Select all filtered beers
            filteredBeers.forEach(beer => {
                const selected = getSelectedCardIds();
                if (!selected.has(beer.id)) {
                    toggleGlobalCardSelection(beer.id);
                }
            });
            analyticsService.trackEvent({
                name: 'filter_applied',
                params: {
                    filter_type: 'select_all',
                    filter_value: String(filteredBeers.length)
                }
            });
        }
    };
    
    // Check if any filters are active
    // Don't show active filters bar when on My Beers tab with Tasting List toggle (showOnlyFavorites = true)
    // since it's obvious they're filtering by tasting list
    const hasActiveFilters = Boolean(
        searchTerm || 
        typeFilter || 
        drinkTypeFilter || 
        breweryFilter || 
        tagFilters.length > 0 ||
        (showOnlyFavorites && !isMyBeersTab) // Only show favorites filter on All Beers tab
    );
    
    // Count active filters
    const activeFilterCount = [
        searchTerm,
        typeFilter,
        drinkTypeFilter,
        breweryFilter,
        showOnlyFavorites ? 'tasting-list' : null,
        ...tagFilters
    ].filter(Boolean).length;
    
    // Get UI state setter to update header visibility and filter bar height
    const { setHasActiveFilters, setFilterBarHeight } = useUiState();
    
    // Ref to measure the filter bar height
    const filterBarRef = useRef<HTMLDivElement>(null);
    
    // Update the global UI state whenever filter status changes
    useEffect(() => {
        setHasActiveFilters(hasActiveFilters);
    }, [hasActiveFilters, setHasActiveFilters]);
    
    // Measure and report filter bar height whenever it changes
    useEffect(() => {
        if (filterBarRef.current && hasActiveFilters) {
            const height = filterBarRef.current.offsetHeight;
            setFilterBarHeight(height);
        } else {
            setFilterBarHeight(0);
        }
    }, [hasActiveFilters, searchTerm, typeFilter, drinkTypeFilter, breweryFilter, tagFilters, showOnlyFavorites, setFilterBarHeight]);
    
    return (
        <Paper 
            ref={filterBarRef}
            sx={{ 
                px: '15px',
                pt: 1.5, 
                pb: 1.5, 
                mb: 0,
                mt: 0, 
                bgcolor: 'var(--primary-colour)',
                backdropFilter: 'blur(10px)',
                borderRadius: 0,
                position: 'fixed',
                top: 0,
                zIndex: 1200,
                boxShadow: hasActiveFilters || isSticky ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.3)',
                transition: 'box-shadow 0.3s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
                left: 0,
                right: 0,
                width: '100%',
                transform: hasActiveFilters || isSticky ? 'translateY(0)' : 'translateY(-100%)',
                pointerEvents: hasActiveFilters || isSticky ? 'auto' : 'none',
                opacity: hasActiveFilters || isSticky ? 1 : 0,
                marginRight: 'calc(-50vw + 50%)',
                color: 'var(--primary-text-colour)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.2)',
            }}
        >
            <Container maxWidth="md">
                {/* Stats and Settings Component */}
                <BeerStatsAndSettings />
                
                {/* Active filters */}
                {hasActiveFilters && (
                    <Box sx={{ mt: 1 }}>
                        {/* Buttons row */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 0 }}>
                            {/* Filter count dropdown button */}
                            <Box
                                onClick={handleFilterMenuOpen}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    cursor: 'pointer',
                                    color: 'var(--secondary-colour)',
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    transition: 'all 0.2s',
                                    borderRadius: '6px',
                                    border: '2px solid var(--secondary-colour)',
                                    backgroundColor: 'var(--primary-colour)',
                                    px: 1.5,
                                    py: 0.75,
                                    '&:hover': {
                                        backgroundColor: 'color-mix(in srgb, var(--primary-colour) 85%, white)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    },
                                }}
                            >
                                <Box>{activeFilterCount} Active {activeFilterCount === 1 ? 'Filter' : 'Filters'}</Box>
                                <ExpandMoreIcon sx={{ fontSize: '1.1rem' }} />
                            </Box>
                            
                            {/* Select All / Deselect All button */}
                            <Box
                                onClick={handleToggleSelectAll}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 0.75,
                                    cursor: 'pointer',
                                    color: allFilteredSelected ? 'var(--primary-colour)' : 'var(--secondary-colour)',
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    transition: 'all 0.2s',
                                    borderRadius: '6px',
                                    border: allFilteredSelected ? '2px solid var(--secondary-colour)' : '2px solid var(--secondary-colour)',
                                    backgroundColor: allFilteredSelected ? 'var(--secondary-colour)' : 'var(--primary-colour)',
                                    whiteSpace: 'nowrap',
                                    px: 1.5,
                                    py: 0.75,
                                    '&:hover': {
                                        backgroundColor: allFilteredSelected ? 'color-mix(in srgb, var(--secondary-colour) 85%, black)' : 'color-mix(in srgb, var(--primary-colour) 85%, white)',
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                    },
                                    '&:active': {
                                        transform: 'translateY(0)',
                                    },
                                }}
                            >
                                {allFilteredSelected ? '✓ Deselect All' : `Select All (${filteredBeers.length})`}
                            </Box>
                        </Box>
                        
                        {/* Filter dropdown menu */}
                        <Menu
                            anchorEl={filterMenuAnchor}
                            open={filterMenuOpen}
                            onClose={handleFilterMenuClose}
                            PaperProps={{
                                sx: {
                                    bgcolor: 'var(--secondary-colour)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    width: filterMenuAnchor?.offsetWidth || 'auto',
                                    mt: 1,
                                }
                            }}
                        >
                            {searchTerm && (
                                <MenuItem onClick={() => { setSearchTerm(''); analyticsService.trackEvent(buildFilterClearedEvent('search')); handleFilterMenuClose(); }} sx={{ color: 'white', fontSize: '0.85rem' }}>
                                    <Box sx={{ flex: 1 }}>{searchTerm}</Box>
                                    <ClearIcon sx={{ fontSize: '0.9rem', ml: 2, color: 'var(--primary-colour)' }} />
                                </MenuItem>
                            )}
                            {typeFilter && (
                                <MenuItem onClick={() => { setTypeFilter(''); analyticsService.trackEvent(buildFilterClearedEvent('style')); handleFilterMenuClose(); }} sx={{ color: 'white', fontSize: '0.85rem' }}>
                                    <Box sx={{ flex: 1 }}>{typeFilter}</Box>
                                    <ClearIcon sx={{ fontSize: '0.9rem', ml: 2, color: 'var(--primary-colour)' }} />
                                </MenuItem>
                            )}
                            {drinkTypeFilter && (
                                <MenuItem onClick={() => { setDrinkTypeFilter(''); analyticsService.trackEvent(buildFilterClearedEvent('drink_type')); handleFilterMenuClose(); }} sx={{ color: 'white', fontSize: '0.85rem' }}>
                                    <Box sx={{ flex: 1 }}>{drinkTypeFilter}</Box>
                                    <ClearIcon sx={{ fontSize: '0.9rem', ml: 2, color: 'var(--primary-colour)' }} />
                                </MenuItem>
                            )}
                            {breweryFilter && (
                                <MenuItem onClick={() => { setBreweryFilter(''); analyticsService.trackEvent(buildFilterClearedEvent('brewery')); handleFilterMenuClose(); }} sx={{ color: 'white', fontSize: '0.85rem' }}>
                                    <Box sx={{ flex: 1 }}>{breweryFilter}</Box>
                                    <ClearIcon sx={{ fontSize: '0.9rem', ml: 2, color: 'var(--primary-colour)' }} />
                                </MenuItem>
                            )}
                            {showOnlyFavorites && (
                                <MenuItem onClick={() => { setShowOnlyFavorites(false); analyticsService.trackEvent(buildFilterClearedEvent('tasting_list')); handleFilterMenuClose(); }} sx={{ color: 'white', fontSize: '0.85rem' }}>
                                    <Box sx={{ flex: 1 }}>Tasting List Only</Box>
                                    <ClearIcon sx={{ fontSize: '0.9rem', ml: 2, color: 'var(--primary-colour)' }} />
                                </MenuItem>
                            )}
                            {tagFilters.map(tag => (
                                <MenuItem key={tag} onClick={() => { removeTagFilter(tag); analyticsService.trackEvent(buildFilterClearedEvent('tag')); handleFilterMenuClose(); }} sx={{ color: 'white', fontSize: '0.85rem' }}>
                                    <Box sx={{ flex: 1 }}>{tag.charAt(0).toUpperCase() + tag.slice(1)}</Box>
                                    <ClearIcon sx={{ fontSize: '0.9rem', ml: 2, color: 'var(--primary-colour)' }} />
                                </MenuItem>
                            ))}
                            <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
                            <MenuItem 
                                onClick={() => { clearAllFilters(); analyticsService.trackEvent(buildFilterClearedEvent('all')); handleFilterMenuClose(); }}
                                sx={{ 
                                    color: 'var(--primary-colour)', 
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    justifyContent: 'center',
                                    '&:hover': {
                                        backgroundColor: 'var(--primary-colour-alpha-20)',
                                    }
                                }}
                            >
                                Clear All Filters
                            </MenuItem>
                        </Menu>
                    </Box>
                )}
            </Container>
        </Paper>
    );
};

export default ActiveFiltersBar;
